import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import moment from 'moment';
import { CloudDownload } from 'lucide-react';
import { ICampaign, ISenderIdentity } from '../../../../../interfaces/models/campaign';
import { useCampaignsRequest } from '../../../../../services/apis/campaigns/useCampaignsRequest';
import { useAppActions } from '../../../../../stores/application/useAppActions';
import DropdownMenu from '../../../../../components/dropdown/DropdownMenu';
import LoadingSpinner from '../../../../../components/LoadingSpinner';
import PerformanceFilters, { IDateRange, LEAD_DATE_PRESETS, rangeForPreset } from './PerformanceFilters';
import WorkspacePerformance from './WorkspacePerformance';
import SendersPerformance from './SendersPerformance';
import { useSavedSenderFilter } from '../useSavedSenderFilter';
import { IPerformanceChartHandle } from './PerformanceChart';
import { ISenderPerformanceRow, IWorkspacePerformance, METRICS, metricsForSequence, PerformanceMetric } from './performanceMetrics';
import { exportSendersCsv, exportSnapshotPng, exportSnapshotSvg, exportWorkspaceCsv } from './exportAnalytics';
import { campaignMobile } from '../../../utils/campaignBreakpoints';

type PerformanceTab = 'workspace' | 'senders';

interface ICampaignPerformanceProps {
  campaigns: ICampaign[];
  /**
   * Single-campaign mode (the campaign page): stats for this campaign only, just the
   * cards its sequence uses, and no campaign filter or Senders tab.
   */
  campaign?: ICampaign;
  identities: ISenderIdentity[];
  /** True once the sender listing has come back, even when it is empty. */
  identitiesLoaded: boolean;
  currentUserId?: string;
  /** The campaign page's jump to its Leads tab, filtered to a card's leads in this range. */
  onOpenLeads?: (metric: PerformanceMetric, range: IDateRange) => void;
}

const CampaignPerformance: FunctionComponent<ICampaignPerformanceProps> = ({ campaigns, campaign, identities, identitiesLoaded, currentUserId, onOpenLeads }) => {
  const { getWorkspacePerformance, getSendersPerformance } = useCampaignsRequest();
  const { setToast } = useAppActions();

  const [tab, setTab] = useState<PerformanceTab>('workspace');
  const [range, setRange] = useState<IDateRange>(() => rangeForPreset('7d'));
  const [campaignIds, setCampaignIds] = useState<string[]>(() => (campaign ? [campaign._id] : []));
  // Nothing is fetched until this settles, so the first request already carries it.
  const [identityIds, setIdentityIds, filtersReady] = useSavedSenderFilter('campaignPerformance.identityIds', identities, identitiesLoaded, currentUserId);
  const [hidden, setHidden] = useState<Set<PerformanceMetric>>(new Set());
  const [workspaceData, setWorkspaceData] = useState<IWorkspacePerformance | null>(null);
  const [senderRows, setSenderRows] = useState<ISenderPerformanceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const chartRef = useRef<IPerformanceChartHandle | null>(null);

  const metrics = useMemo(
    () => (campaign ? metricsForSequence(campaign.sequence?.map(s => s.type) ?? []) : METRICS),
    [campaign],
  );

  // "All time" has no start of its own: the server begins at the first event in scope
  // and answers with the dates it used, which the subtitle and exports then show.
  const [resolved, setResolved] = useState<{ from: string; to: string } | null>(null);
  const period = resolved ?? { from: range.from, to: range.to };

  // Shown only when a load takes a moment, so quick responses don't flash.
  const [showLoader, setShowLoader] = useState(false);
  useEffect(() => {
    if (!loading) { setShowLoader(false); return; }
    const timer = window.setTimeout(() => setShowLoader(true), 250);
    return () => window.clearTimeout(timer);
  }, [loading]);

  useEffect(() => {
    if (!filtersReady) return;
    let stale = false;
    const query = {
      from: range.preset === 'all' ? 'all' : range.from,
      to: range.preset === 'all' ? moment().format('YYYY-MM-DD') : range.to,
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      campaignIds,
      identityIds,
    };
    setLoading(true);
    (async () => {
      if (tab === 'workspace') {
        const res: any = await getWorkspacePerformance(query);
        if (stale) return;
        const payload = res?.data ?? res;
        if (payload?.series) { setWorkspaceData(payload); setResolved({ from: payload.from, to: payload.to }); }
        else setToast({ type: 'error', message: res?.message || 'Could not load performance.' });
      } else {
        const res: any = await getSendersPerformance(query);
        if (stale) return;
        const payload = res?.data ?? res;
        if (Array.isArray(payload?.senders)) { setSenderRows(payload.senders); setResolved({ from: payload.from, to: payload.to }); }
        else setToast({ type: 'error', message: res?.message || 'Could not load sender performance.' });
      }
      setLoading(false);
    })();
    return () => { stale = true; };
    // The request helpers are rebuilt every render by the request hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, range, campaignIds, identityIds, filtersReady]);

  function toggleMetric(metric: PerformanceMetric) {
    setHidden(prev => {
      const next = new Set(prev);
      if (next.has(metric)) next.delete(metric); else next.add(metric);
      return next;
    });
  }

  const subtitle = `${range.preset === 'all' ? 'All time · ' : ''}${moment(period.from).format('MMM D, YYYY')} – ${moment(period.to).format('MMM D, YYYY')}`;

  async function handleExport(format: 'csv' | 'svg' | 'png') {
    setExportOpen(false);
    if (tab === 'senders') {
      if (format === 'csv') exportSendersCsv(senderRows, period);
      return;
    }
    if (!workspaceData) return;
    if (format === 'csv') { exportWorkspaceCsv(workspaceData, metrics); return; }
    const snapshot = {
      data: workspaceData,
      metrics,
      title: campaign ? `${campaign.name} · Performance` : 'Performance',
      hidden,
      subtitle,
      chartImage: chartRef.current?.toImage() ?? null,
      chartSize: chartRef.current?.size() ?? { width: 0, height: 0 },
    };
    try {
      if (format === 'svg') exportSnapshotSvg(snapshot);
      else await exportSnapshotPng(snapshot);
    } catch {
      setToast({ type: 'error', message: 'Could not export analytics.' });
    }
  }

  const imageExportDisabled = tab !== 'workspace' || !workspaceData;

  return (
    <Panel>
      <ExportMenuGlobalStyles />
      <PanelHeader>
        <PanelTitle>Performance</PanelTitle>
        <DropdownMenu
          className="performance-export"
          align="end"
          $hideToggle
          hideArrow
          show={exportOpen}
          onToggle={setExportOpen}
          title={(
            <ExportTrigger>
              <CloudDownload size={20} strokeWidth={1.5} />
              <span>Export analytics</span>
            </ExportTrigger>
          )}
        >
          <ExportMenu>
            <ExportOption type="button" onClick={() => void handleExport('csv')}>Export as CSV</ExportOption>
            <ExportOption type="button" disabled={imageExportDisabled} onClick={() => void handleExport('svg')}>Export as SVG</ExportOption>
            <ExportOption type="button" disabled={imageExportDisabled} onClick={() => void handleExport('png')}>Export as PNG</ExportOption>
            {tab === 'senders' && <ExportHint>SVG and PNG export the Workspace performance stats and chart.</ExportHint>}
          </ExportMenu>
        </DropdownMenu>
      </PanelHeader>

      <Body>
        {!campaign && (
        <SubTabs role="tablist">
          <SubTab type="button" role="tab" aria-selected={tab === 'workspace'} $active={tab === 'workspace'} onClick={() => setTab('workspace')}>
            Workspace performance
          </SubTab>
          <SubTab type="button" role="tab" aria-selected={tab === 'senders'} $active={tab === 'senders'} onClick={() => setTab('senders')}>
            Senders performance
          </SubTab>
        </SubTabs>
        )}

        <Section>
          <PerformanceFilters
            range={range}
            onRangeChange={setRange}
            presets={LEAD_DATE_PRESETS}
            campaigns={campaigns}
            campaignIds={campaignIds}
            onCampaignIdsChange={setCampaignIds}
            identities={identities}
            identityIds={identityIds}
            onIdentityIdsChange={setIdentityIds}
            currentUserId={currentUserId}
            showCampaignFilter={!campaign}
          />

          {/* The previous results stay up, dimmed, until the new ones arrive. */}
          <LoadingArea $loading={showLoader} aria-busy={loading}>
            {tab === 'workspace' ? (
              <WorkspacePerformance
                ref={chartRef}
                data={workspaceData}
                metrics={metrics}
                loading={loading}
                hidden={hidden}
                onToggleMetric={toggleMetric}
                onOpenLeads={onOpenLeads ? metric => onOpenLeads(metric, range) : undefined}
              />
            ) : (
              <SendersPerformance rows={senderRows} loading={loading} />
            )}
            {showLoader && (
              <LoaderOverlay>
                <LoadingSpinner height="28px" width="28px" />
              </LoaderOverlay>
            )}
          </LoadingArea>
        </Section>
      </Body>
    </Panel>
  );
};

export default CampaignPerformance;

const ExportMenuGlobalStyles = createGlobalStyle`
  .performance-export .actions-dropdown + div,
  .performance-export .dropdown-menu {
    padding: 0;
    border: 1px solid #e8eaed;
    border-radius: 10px;
    box-shadow: 0px 0px 6px rgba(51, 57, 81, 0.1);
    background: #fff;
    min-width: 200px;
  }
  .performance-export .dropdown-toggle::after,
  .performance-export .dropdown-toggle::before { display: none !important; content: none !important; }
`;

const Panel = styled.div`
  background: #FFFFFF;
  border: 1px solid #E9EAEB;
  border-radius: 12px;
  display: flex; flex-direction: column;
`;

const PanelHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 24px 24px 16px;
  ${campaignMobile`padding: 16px 16px 12px;`}
`;

const PanelTitle = styled.span`
  font-family: 'Nunito Sans', NunitoSans, sans-serif; font-size: 20px; font-weight: 700; line-height: 30px;
  color: #111927;
`;

const ExportTrigger = styled.span`
  display: inline-flex; align-items: center; gap: 6px;
  height: 39px; padding: 0 14px;
  background: #FFFFFF; border: 1px solid #E1E5EB; border-radius: 8px;
  font-family: 'Nunito Sans', NunitoSans, sans-serif; font-size: 14px; line-height: 20px;
  color: #414651; white-space: nowrap;
  svg { color: #8C96AD; }
  &:hover { background: #F9FAFB; }
`;

const ExportMenu = styled.div`
  padding: 8px; display: flex; flex-direction: column;
`;

const ExportOption = styled.button`
  border: none; background: transparent; text-align: left;
  padding: 8px 10px; border-radius: 6px;
  font-family: NunitoSans; font-size: 14px; color: #41495e; cursor: pointer;
  &:hover:not(:disabled) { background: #F3F4F6; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const ExportHint = styled.span`
  max-width: 220px; padding: 6px 10px 2px;
  font-family: NunitoSans; font-size: 12px; line-height: 16px; color: #8C96AD;
`;

const Body = styled.div`
  display: flex; flex-direction: column; gap: 24px;
  padding-bottom: 8px;
`;

const SubTabs = styled.div`
  display: flex; gap: 24px;
  padding: 0 24px;
  border-bottom: 1px solid #E5E7EB;
  ${campaignMobile`padding: 0 16px;`}
`;

const SubTab = styled.button<{ $active: boolean }>`
  position: relative;
  padding: 0 4px 12px;
  border: none; background: transparent; cursor: pointer;
  font-family: 'Nunito Sans', NunitoSans, sans-serif; font-size: 14px; font-weight: 600; line-height: 20px;
  color: ${({ $active }) => ($active ? '#454DE5' : '#6B7280')};

  &::after {
    content: '';
    position: absolute; left: 0; right: 0; bottom: -1px;
    height: 2px;
    background: ${({ $active }) => ($active ? '#454DE5' : 'transparent')};
  }

  &:hover { color: #454DE5; }
`;

const Section = styled.div`
  display: flex; flex-direction: column; gap: 16px;
`;

const LoaderOverlay = styled.div`
  position: absolute; inset: 0; z-index: 6;
  display: flex; align-items: center; justify-content: center;
`;

// Dims every child — cards and chart are siblings here — except the spinner itself.
const LoadingArea = styled.div<{ $loading: boolean }>`
  position: relative;
  display: flex; flex-direction: column; gap: 16px;

  & > *:not(${LoaderOverlay}) {
    opacity: ${({ $loading }) => ($loading ? 0.5 : 1)};
    transition: opacity 0.15s ease;
    pointer-events: ${({ $loading }) => ($loading ? 'none' : 'auto')};
  }
`;
